import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  usePayoutBalance,
  useRequestPayout,
  useSetETransferEmail,
} from '../hooks/usePayouts.js';
import { SpinnerButton } from '../components/SpinnerButton.js';

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function AccountPayoutsPage() {
  const { data, isLoading } = usePayoutBalance();
  const setEmail = useSetETransferEmail();
  const request = useRequestPayout();
  const [emailInput, setEmailInput] = useState('');

  if (isLoading || !data) {
    return <div className="mx-auto max-w-2xl px-4 py-16 text-navy-500">Loading…</div>;
  }

  const remainingToThreshold = Math.max(data.thresholdCents - data.balanceCents, 0);
  const pctOfThreshold = Math.min(100, (data.balanceCents / data.thresholdCents) * 100);
  const pendingRequest = data.history.some((h) => h.status === 'requested');

  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <Link to="/account" className="text-sm text-navy-500 hover:text-navy">
        ← Back to account
      </Link>
      <h1 className="text-3xl font-bold text-navy mt-4 mb-2">Payouts</h1>
      <p className="text-navy-600 mb-8">
        Earn money on your trailer rentals. When your balance reaches{' '}
        <strong>{money(data.thresholdCents)}</strong>, we send your earnings via{' '}
        Interac e-Transfer.
      </p>

      <div className="card p-6 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm text-navy-500 uppercase tracking-wider font-semibold">
            Current balance
          </span>
          <span className="text-xs text-navy-500">
            Threshold: {money(data.thresholdCents)}
          </span>
        </div>
        <div className="text-4xl font-bold text-navy mb-3">{money(data.balanceCents)}</div>
        <div className="w-full h-2 bg-navy-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-orange transition-all"
            style={{ width: `${pctOfThreshold}%` }}
          />
        </div>
        {data.balanceCents < data.thresholdCents ? (
          <p className="text-sm text-navy-500 mt-3">
            Earn another <strong>{money(remainingToThreshold)}</strong> to unlock payouts.
            Your earnings keep accruing — they don't expire.
          </p>
        ) : (
          <p className="text-sm text-green-700 mt-3">
            You're eligible for a payout. Add your e-Transfer email below to request it.
          </p>
        )}
      </div>

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-navy mb-1">e-Transfer email</h2>
        <p className="text-sm text-navy-500 mb-4">
          We send payouts here. Make sure auto-deposit is set up on your bank so the funds
          arrive instantly. <strong>You only need this when you're ready to cash out</strong> —
          you can list and earn before adding it.
        </p>
        {data.eTransferEmail ? (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-navy-50 rounded-lg">
            <span className="font-mono text-sm text-navy-800">{data.eTransferEmail}</span>
            <button
              className="text-sm text-orange hover:underline"
              onClick={() => setEmailInput(data.eTransferEmail ?? '')}
            >
              Change
            </button>
          </div>
        ) : null}
        {(emailInput || !data.eTransferEmail) && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!emailInput) return;
              await setEmail.mutateAsync(emailInput.trim());
              setEmailInput('');
            }}
            className="flex gap-2 mt-3"
          >
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="your-etransfer@example.com"
              className="input flex-1"
              required
            />
            <SpinnerButton
              className="btn-primary"
              loading={setEmail.isPending}
              disabled={!emailInput}
            >
              Save
            </SpinnerButton>
          </form>
        )}
      </div>

      <SpinnerButton
        className="btn-primary w-full"
        loading={request.isPending}
        disabled={
          !data.eligibleToRequest ||
          !data.eTransferEmail ||
          pendingRequest
        }
        onClick={() => request.mutate()}
      >
        {pendingRequest
          ? 'Payout requested — processing'
          : !data.eligibleToRequest
            ? `Earn ${money(remainingToThreshold)} more to request`
            : !data.eTransferEmail
              ? 'Add e-Transfer email first'
              : `Request ${money(data.balanceCents)} payout`}
      </SpinnerButton>

      <p className="text-xs text-navy-400 text-center mt-3">
        Payouts are sent within 2 business days of being requested. Banking holidays may delay
        delivery.
      </p>

      {data.history.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-navy mb-3">Payout history</h2>
          <div className="card divide-y divide-navy-50 overflow-hidden">
            {data.history.map((h) => (
              <div key={h.id} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-navy">{money(h.amountCents)}</div>
                  <div className="text-xs text-navy-500">
                    {new Date(h.createdAt).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <span
                  className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${
                    h.status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : h.status === 'requested'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-navy-100 text-navy-700'
                  }`}
                >
                  {h.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
